import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2, ClipboardCheck } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { ProLocked, CompanyPicker } from "../components/ProGate";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { canPlan, hasPro, isProRequired, useProScope } from "../lib/pro";

export default function Checklists() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const scope = useProScope(user);
  const [types, setTypes] = useState([]);
  const [sel, setSel] = useState("");
  const [items, setItems] = useState([]);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user && hasPro(user) && !canPlan(user)) nav("/planning", { replace: true }); }, [user, nav]);

  const load = (keep) => {
    if (!canPlan(user) || scope.needsCompany) return;
    setLoading(true);
    api.get("/pro/machine-types", { params: scope.params })
      .then(({ data }) => {
        setTypes(data); setLocked(false);
        const id = keep && data.some((x) => x.id === keep) ? keep : data[0]?.id || "";
        setSel(id); setItems((data.find((x) => x.id === id)?.checklist || []).map((i) => ({ ...i })));
      })
      .catch((e) => { if (isProRequired(e)) setLocked(true); else toast.error(apiError(e)); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [user, scope.cid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasPro(user)) return <><PageHeader title={t("Checklists")} /><ProLocked /></>;

  const pick = (id) => { setSel(id); setItems((types.find((x) => x.id === id)?.checklist || []).map((i) => ({ ...i }))); };
  const upd = (i, patch) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= items.length) return; const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; setItems(n); };
  const save = async () => {
    if (items.some((x) => !x.label.trim())) return toast.error(t("Every item needs a label"));
    setSaving(true);
    try {
      await api.put(`/pro/machine-types/${sel}/checklist`, { items: items.map((x) => ({ id: x.id || null, label: x.label.trim(), required: !!x.required })) }, { params: scope.params });
      toast.success(t("Checklist saved")); load(sel);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title={t("Checklists")} subtitle={t("One checklist template per machine type")} />
      {scope.isSA && <CompanyPicker scope={scope} />}
      {scope.needsCompany ? <p className="text-zinc-500 px-4 md:px-0 mt-4 text-sm">{t("Choose a company to edit its checklists.")}</p> : locked ? <ProLocked /> : loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div> : types.length === 0 ? (
        <p className="text-zinc-500 px-4 md:px-0 mt-6 text-sm" data-testid="checklists-empty">{t("No machine types yet.")}</p>
      ) : (
        <div className="px-4 md:px-0 pt-4 md:grid md:grid-cols-[240px_1fr] md:gap-6">
          <div className="flex md:flex-col gap-2 overflow-x-auto pb-2" data-testid="checklist-type-list">
            {types.map((mt) => (
              <button key={mt.id} onClick={() => pick(mt.id)} data-testid={`checklist-type-${mt.id}`}
                className={`shrink-0 text-left px-4 py-3 rounded-xl border transition-colors ${sel === mt.id ? "border-[#F26722] bg-[#3B1D0E]/40 text-white" : "border-zinc-800 bg-[#18181B] text-zinc-300 hover:border-zinc-700"}`}>
                <p className="font-bold text-sm">{mt.name}</p>
                <p className="text-[11px] text-zinc-500">{(mt.checklist || []).length} {t("items")}</p>
              </button>
            ))}
          </div>
          <div className="mt-3 md:mt-0" data-testid="checklist-editor">
            <p className="text-xs font-bold text-[#F26722] uppercase tracking-wider flex items-center gap-1.5"><ClipboardCheck size={14} /> {types.find((x) => x.id === sel)?.name}</p>
            <div className="space-y-2 mt-3">
              {items.length === 0 && <p className="text-sm text-zinc-500" data-testid="checklist-no-items">{t("No checklist items yet.")}</p>}
              {items.map((x, i) => (
                <div key={x.id || `new-${i}`} className="flex items-center gap-2 bg-[#18181B] border border-zinc-800 rounded-xl p-2.5" data-testid={`checklist-item-${i}`}>
                  <span className="text-xs text-zinc-600 w-5 text-center">{i + 1}</span>
                  <input className="flex-1 min-w-0 bg-[#1c1c1f] border border-zinc-800 text-white rounded-lg px-3 py-2 text-sm focus:border-[#F26722] focus:outline-none" value={x.label} placeholder={t("e.g. Check oil level")} onChange={(e) => upd(i, { label: e.target.value })} data-testid={`checklist-item-label-${i}`} />
                  <label className="flex items-center gap-1.5 text-xs text-zinc-400 shrink-0 cursor-pointer">
                    <input type="checkbox" checked={!!x.required} onChange={(e) => upd(i, { required: e.target.checked })} className="accent-[#F26722] w-4 h-4" data-testid={`checklist-item-required-${i}`} /> {t("Required")}
                  </label>
                  <button onClick={() => move(i, -1)} className="text-zinc-500 hover:text-white p-1" data-testid={`checklist-item-up-${i}`}><ArrowUp size={15} /></button>
                  <button onClick={() => move(i, 1)} className="text-zinc-500 hover:text-white p-1" data-testid={`checklist-item-down-${i}`}><ArrowDown size={15} /></button>
                  <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-400 p-1" data-testid={`checklist-item-delete-${i}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setItems([...items, { id: null, label: "", required: false }])} data-testid="checklist-add-item" className="w-full border border-dashed border-zinc-700 text-[#F26722] rounded-xl py-3 mt-3 font-semibold flex items-center justify-center gap-2"><Plus size={18} /> {t("Add item")}</button>
            <button onClick={save} disabled={saving} data-testid="checklist-save-btn" className="w-full bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white font-bold py-3.5 rounded-xl mt-3 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> {t("Save checklist")}</>}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
